// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: preset statuses (Available / Busy / Focus / In a meeting / Out of
// office) built on top of the base status + custom status primitives.

import type {Icon} from '@app/features/ui/icons/Icons';
import {
	CalendarDotsIcon,
	CheckCircleIcon,
	PhoneCallIcon,
	ProhibitIcon,
	TargetIcon,
	TreePalmIcon,
} from '@app/features/ui/icons/Icons';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import type {CustomStatus} from '@app/features/user/state/CustomStatus';
import type {StatusType} from '@konvo/constants/src/StatusConstants';
import {StatusTypes} from '@konvo/constants/src/StatusConstants';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export interface KonvoStatusPreset {
	id: 'available' | 'busy' | 'focus' | 'meeting' | 'call' | 'ooo';
	label: MessageDescriptor;
	status: StatusType;
	/** Icon shown in the picker rows. */
	icon: Icon;
	/** Custom status text; null clears any custom status. */
	text: string | null;
}

export const KONVO_STATUS_PRESETS: ReadonlyArray<KonvoStatusPreset> = [
	{
		id: 'available',
		label: msg({message: 'Available', comment: 'Preset status: online, clears any custom status.'}),
		status: StatusTypes.ONLINE,
		icon: CheckCircleIcon,
		text: null,
	},
	{
		id: 'busy',
		label: msg({message: 'Busy', comment: 'Preset status: do not disturb with a Busy label.'}),
		status: StatusTypes.DND,
		icon: ProhibitIcon,
		text: 'Busy',
	},
	{
		id: 'focus',
		label: msg({message: 'Focus', comment: 'Preset status: do not disturb while focusing.'}),
		status: StatusTypes.DND,
		icon: TargetIcon,
		text: 'Focus',
	},
	{
		id: 'meeting',
		label: msg({message: 'In a meeting', comment: 'Preset status: do not disturb while in a meeting.'}),
		status: StatusTypes.DND,
		icon: CalendarDotsIcon,
		text: 'In a meeting',
	},
	{
		id: 'call',
		label: msg({message: 'In a call', comment: 'Preset status: do not disturb while on a Teams or Konvo call.'}),
		status: StatusTypes.DND,
		icon: PhoneCallIcon,
		text: 'In a call',
	},
	{
		id: 'ooo',
		label: msg({message: 'Out of office', comment: 'Preset status: away/idle while out of office.'}),
		status: StatusTypes.IDLE,
		icon: TreePalmIcon,
		text: 'Out of office',
	},
];

export function presetCustomStatus(preset: KonvoStatusPreset): CustomStatus | null {
	if (!preset.text) {
		return null;
	}
	return {
		text: preset.text,
		expiresAt: null,
		emojiId: null,
		emojiName: null,
		emojiAnimated: null,
	};
}

export async function applyKonvoStatusPreset(preset: KonvoStatusPreset): Promise<void> {
	await UserSettingsCommands.update({
		status: preset.status,
		customStatus: presetCustomStatus(preset),
		statusResetsAt: null,
		statusResetsTo: null,
	});
}
